import { createContext, useContext } from "react";
import type { Activity, IncidentProposal, StatusError } from "../../server/src/shared-types";
import type { ConfirmationOutcome } from "./timeline";

export type Language = "pt" | "en" | "es";

/** Teams locale (e.g. `pt-br`) or browser language; anything other than pt/es falls back to English. */
export function detectLanguage(locale: string | undefined): Language {
  const tag = (locale ?? navigator.language ?? "").toLowerCase();
  if (tag.startsWith("pt")) return "pt";
  if (tag.startsWith("es")) return "es";
  return "en";
}

export interface Messages {
  title: string;
  subtitle: string;
  newReport: string;
  greeting: (name: string) => string;
  intro: string;
  languages: string;
  examples: string[];
  messageLabel: string;
  send: string;
  placeholder: { default: string; pendingCard: string; ended: string };
  errors: { boot: string; send: string; confirm: string };
  activity: (activity: Activity, issue?: string) => string;
  status: Record<StatusError, string>;
  typing: string;
  priority: Record<IncidentProposal["priority"], string>;
  card: {
    label: string;
    eyebrow: string;
    system: string;
    impact: string;
    started: string;
    workaround: string;
    why: (priority: string) => string;
    outcome: Record<ConfirmationOutcome, string>;
    adjustQuestion: string;
    adjustPlaceholder: string;
    back: string;
    sendAdjustment: string;
    adjust: string;
    opening: string;
    open: string;
  };
  incident: { created: string; commented: string };
}

const pt: Messages = {
  title: "Incidentes de tecnologia",
  subtitle: "Plataforma fora do ar ou lenta? Conte aqui.",
  newReport: "Novo relato",
  greeting: (name) => `Oi, ${name}!`,
  intro:
    "Descreva o que está acontecendo. Eu verifico no Linear se o incidente já está sendo tratado e, se não estiver, abro um novo com a prioridade certa.",
  languages: "Pode escrever em português, inglês ou espanhol.",
  examples: [
    "O checkout está retornando erro 500 para todos os clientes",
    "A plataforma está muito lenta desde as 14h",
    "As integrações com o parceiro pararam de sincronizar",
  ],
  messageLabel: "Mensagem",
  send: "Enviar",
  placeholder: {
    default: "Descreva o incidente…",
    pendingCard: "Responda à proposta acima para continuar",
    ended: "Inicie um novo relato",
  },
  errors: {
    boot: "Não foi possível entrar com sua conta Movik. Feche e abra o app novamente.",
    send: "Não consegui enviar sua mensagem. Tente de novo.",
    confirm: "Não consegui registrar sua resposta. Tente de novo.",
  },
  activity: (activity, issue) =>
    activity === "searching"
      ? "Consultando incidentes abertos no Linear…"
      : `Adicionando seu relato ao incidente ${issue ?? ""}…`,
  status: {
    agent_failed: "O agente encontrou um erro e não conseguiu continuar.",
    budget_reached: "Esta conversa atingiu o limite de uso. Inicie um novo relato.",
    refusal: "O agente não pôde continuar esta conversa.",
    terminated: "Esta conversa foi encerrada. Inicie um novo relato.",
    temporary: "O agente encontrou um erro. Tente novamente em instantes.",
  },
  typing: "O agente está respondendo",
  priority: { 1: "Urgente", 2: "Alta", 3: "Média", 4: "Baixa" },
  card: {
    label: "Proposta de novo incidente",
    eyebrow: "Novo incidente",
    system: "Sistema",
    impact: "Impacto",
    started: "Início",
    workaround: "Workaround",
    why: (p) => `Por que ${p}:`,
    outcome: { approved: "Confirmado", rejected: "Você pediu ajustes", failed: "Não foi possível abrir", withdrawn: "" },
    adjustQuestion: "O que precisa mudar?",
    adjustPlaceholder: "Ex.: a prioridade deveria ser P1, afeta todos os clientes",
    back: "Voltar",
    sendAdjustment: "Enviar ajuste",
    adjust: "Ajustar",
    opening: "Abrindo…",
    open: "Abrir incidente",
  },
  incident: { created: "Incidente aberto", commented: "Relato adicionado a" },
};

const en: Messages = {
  title: "Technology incidents",
  subtitle: "Platform down or slow? Tell me here.",
  newReport: "New report",
  greeting: (name) => `Hi, ${name}!`,
  intro:
    "Describe what's happening. I'll check Linear for an incident that's already being handled and, if there isn't one, open a new one with the right priority.",
  languages: "You can write in English, Portuguese or Spanish.",
  examples: [
    "Checkout is returning error 500 for every customer",
    "The platform has been very slow since 2 pm",
    "The partner integrations stopped syncing",
  ],
  messageLabel: "Message",
  send: "Send",
  placeholder: {
    default: "Describe the incident…",
    pendingCard: "Answer the proposal above to continue",
    ended: "Start a new report",
  },
  errors: {
    boot: "We couldn't sign you in with your Movik account. Close and reopen the app.",
    send: "I couldn't send your message. Please try again.",
    confirm: "I couldn't record your answer. Please try again.",
  },
  activity: (activity, issue) =>
    activity === "searching" ? "Checking open incidents in Linear…" : `Adding your report to incident ${issue ?? ""}…`,
  status: {
    agent_failed: "The agent ran into an error and couldn't continue.",
    budget_reached: "This conversation reached its usage limit. Start a new report.",
    refusal: "The agent couldn't continue this conversation.",
    terminated: "This conversation has ended. Start a new report.",
    temporary: "The agent ran into an error. Please try again in a moment.",
  },
  typing: "The agent is replying",
  priority: { 1: "Urgent", 2: "High", 3: "Medium", 4: "Low" },
  card: {
    label: "New incident proposal",
    eyebrow: "New incident",
    system: "System",
    impact: "Impact",
    started: "Started",
    workaround: "Workaround",
    why: (p) => `Why ${p}:`,
    outcome: { approved: "Confirmed", rejected: "You asked for changes", failed: "Couldn't open it", withdrawn: "" },
    adjustQuestion: "What needs to change?",
    adjustPlaceholder: "E.g. the priority should be P1, it affects every customer",
    back: "Back",
    sendAdjustment: "Send changes",
    adjust: "Adjust",
    opening: "Opening…",
    open: "Open incident",
  },
  incident: { created: "Incident opened", commented: "Report added to" },
};

const es: Messages = {
  title: "Incidentes de tecnología",
  subtitle: "¿La plataforma está caída o lenta? Cuéntamelo aquí.",
  newReport: "Nuevo reporte",
  greeting: (name) => `¡Hola, ${name}!`,
  intro:
    "Describe lo que está pasando. Reviso en Linear si el incidente ya se está atendiendo y, si no, abro uno nuevo con la prioridad correcta.",
  languages: "Puedes escribir en español, inglés o portugués.",
  examples: [
    "El checkout está devolviendo error 500 a todos los clientes",
    "La plataforma está muy lenta desde las 14 h",
    "Las integraciones con el socio dejaron de sincronizar",
  ],
  messageLabel: "Mensaje",
  send: "Enviar",
  placeholder: {
    default: "Describe el incidente…",
    pendingCard: "Responde a la propuesta de arriba para continuar",
    ended: "Inicia un nuevo reporte",
  },
  errors: {
    boot: "No pudimos iniciar sesión con tu cuenta de Movik. Cierra y vuelve a abrir la app.",
    send: "No pude enviar tu mensaje. Inténtalo de nuevo.",
    confirm: "No pude registrar tu respuesta. Inténtalo de nuevo.",
  },
  activity: (activity, issue) =>
    activity === "searching"
      ? "Consultando incidentes abiertos en Linear…"
      : `Agregando tu reporte al incidente ${issue ?? ""}…`,
  status: {
    agent_failed: "El agente encontró un error y no pudo continuar.",
    budget_reached: "Esta conversación alcanzó su límite de uso. Inicia un nuevo reporte.",
    refusal: "El agente no pudo continuar esta conversación.",
    terminated: "Esta conversación terminó. Inicia un nuevo reporte.",
    temporary: "El agente encontró un error. Inténtalo de nuevo en unos instantes.",
  },
  typing: "El agente está respondiendo",
  priority: { 1: "Urgente", 2: "Alta", 3: "Media", 4: "Baja" },
  card: {
    label: "Propuesta de nuevo incidente",
    eyebrow: "Nuevo incidente",
    system: "Sistema",
    impact: "Impacto",
    started: "Inicio",
    workaround: "Solución temporal",
    why: (p) => `Por qué ${p}:`,
    outcome: { approved: "Confirmado", rejected: "Pediste cambios", failed: "No se pudo abrir", withdrawn: "" },
    adjustQuestion: "¿Qué hay que cambiar?",
    adjustPlaceholder: "Ej.: la prioridad debería ser P1, afecta a todos los clientes",
    back: "Volver",
    sendAdjustment: "Enviar cambios",
    adjust: "Ajustar",
    opening: "Abriendo…",
    open: "Abrir incidente",
  },
  incident: { created: "Incidente abierto", commented: "Reporte agregado a" },
};

export const MESSAGES: Record<Language, Messages> = { pt, en, es };

export const I18nContext = createContext<Messages>(en);

export function useMessages(): Messages {
  return useContext(I18nContext);
}
